import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { List, ListItem, ListItemText, Divider, Box, Typography, Dialog, DialogTitle, DialogContent } from '@mui/material';
import InfoTooltip from './InfoTooltip';
import api from '../functions/api';
import rehypeRaw from 'rehype-raw';
import ReactMarkdown from 'react-markdown';
import MarketAnalysisModal from './MarketAnalysisModal';

// DAS-2310: contrarian-direction tooltips for the new Fundamental vector labels.
// Without these, a user sees "Broad EPS Growth – Late Cycle" and reads it as
// bullish, which is exactly backwards — the composite score is -2 because
// broad strength is already priced in. Tooltip must surface the direction.
const FUNDAMENTAL_CONTRARIAN_TOOLTIPS = {
  'Broad EPS Growth – Late Cycle':
    '⚠️ Contrarian BEARISH — ≥70% of top-300 S&P 500 have higher trailing EPS than 1yr ago. ' +
    'Broad strength already priced in. Validated: avg next-90d S&P return +1.0%, 63% hit rate. Score -2.',
  'Healthy EPS Expansion':
    '✅ BULLISH — 60–70% of universe growing EPS YoY. Empirically the strongest bucket: ' +
    'avg next-90d S&P return +5.4%, 89% hit rate. Score +2.',
  'Moderate EPS Expansion':
    '🟢 Mildly bullish — 50–60% of universe growing EPS YoY. Score +1.',
  'Mixed EPS Direction':
    '🟡 Mixed — 40–50% of universe growing EPS YoY. Score +1 (contrarian lean).',
  'Broad EPS Contraction':
    '⚫ Rare / muted — <40% growing EPS YoY. Too few observations in validation to score confidently. Score 0.',
};

const fundamentalContrarianTooltip = (value) => {
  if (typeof value !== 'string') return null;
  for (const [label, tip] of Object.entries(FUNDAMENTAL_CONTRARIAN_TOOLTIPS)) {
    if (value.includes(label)) return tip;
  }
  return null;
};

const VectorsTable = () => {
  const vectors = useSelector(state => state.portfolio?.data?.vectors);
  const vectorsPrevious = useSelector(state => state.portfolio?.data?.Vectors_previous || {});
  // console.log("vectors", vectors);
  const [selectedVector, setSelectedVector] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [open, setOpen] = useState(false);

  if (!vectors || vectors.length === 0) {
    return <div>No data available</div>;
  }

  const getRowColor = (value) => {
    if (typeof value === 'string') {
      if (value.includes('No Panic') || value.includes('Stable')|| value.includes('Bullish')
        || value.includes('Strong') || value.includes('Strengthening') || value.includes('Market Has Strong Support')
      
      ) return '#DFF2BF'; // Green

    

      if (value.includes('Weakening') || value.includes('Moderate Drop Risk')|| value.includes('Caution')
        || value.includes('No CLear') || value.includes('Inconclusive') || value.includes('Equilibrium')
      ) return '#FEEFB3'; // Orange


      if (value.includes('Bearish') || value.includes('Weak') || value.includes('Panic behaviour')
        || value.includes('Stress')|| value.includes('Deep Drop Risk')
      ) return '#FF4C4C'; // Red
;
    }
    return 'transparent';
  };

  const getVectorChangeIndicator = (vectorName, currentValue) => {
    const previousData = vectorsPrevious[vectorName];
    if (!previousData) return null;
    
    const previousValue = previousData.Vector_value;
    if (previousValue === currentValue) return null; // No change
    
    // Determine change direction based on market sentiment
    const isBullish = (value) => value.toLowerCase().includes('bullish') || 
                               value.toLowerCase().includes('strong') || 
                               value.toLowerCase().includes('no panic') ||
                               value.toLowerCase().includes('stable');
    
    const isBearish = (value) => value.toLowerCase().includes('bearish') || 
                                value.toLowerCase().includes('weak') || 
                                value.toLowerCase().includes('panic') ||
                                value.toLowerCase().includes('stress') ||
                                value.toLowerCase().includes('drop risk');
    
    if (isBullish(currentValue) && isBearish(previousValue)) {
      return { type: 'improved', color: '#90EE90', symbol: '↗', tooltip: `up from previously "${previousValue}"` };
    } else if (isBearish(currentValue) && isBullish(previousValue)) {
      return { type: 'worsened', color: '#FF6B6B', symbol: '↘', tooltip: `down from previously "${previousValue}"` };
    } else if (isBullish(currentValue) && !isBullish(previousValue) && !isBearish(previousValue)) {
      return { type: 'improved', color: '#90EE90', symbol: '↗', tooltip: `up from previously "${previousValue}"` };
    } else if (isBearish(currentValue) && !isBearish(previousValue) && !isBullish(previousValue)) {
      return { type: 'worsened', color: '#FF6B6B', symbol: '↘', tooltip: `down from previously "${previousValue}"` };
    }
    
    return { type: 'changed', color: '#FFD700', symbol: '↔', tooltip: `changed from previously "${previousValue}"` };
  };

  const handleRowClick = async (vectorName) => {
    setSelectedVector(vectorName);
    setOpen(true);
    console.log("vectorName", vectorName);

    try {
      let analysis_type = vectorName.startsWith("Institutional_trend")
        ? "Institutional_trend": vectorName;
      
      // Map specific vector names to valid analysis types
      // Be tolerant to label variations (e.g., extra words like "Analysis")
      const nameLower = (vectorName || "").toLowerCase();
      // DAS-2310: route new EPS-momentum labels to the Fundamental analysis
      // type. Legacy "eps surprises" kept for historical rows.
      if (
        nameLower.includes("eps surprises") ||
        nameLower.includes("eps growth") ||
        nameLower.includes("eps expansion") ||
        nameLower.includes("eps contraction") ||
        nameLower.includes("eps direction")
      ) {
        analysis_type = "Fundamental";
      }

      const response = await api.post("/Market_analysis", { analysis_type });

      setAnalysisData(response.data);
      console.log("analysisData", response.data);
    } catch (error) {
      setAnalysisData({ error: "Failed to retrieve analysis" });
    }
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ width: 'fit-content', overflow: 'hidden',padding: '2px' }}>
        <Table size="small" aria-label="dense vectors table">
          <TableHead>
            <TableRow>
              <TableCell 
                sx={{ fontSize: '10px', fontWeight: 'bold', padding: '0.1px', cursor: 'pointer' }}
                onClick={() => handleRowClick('Market')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Marketstatus
                  <InfoTooltip 
                    text="Multi-layered market health analysis combining institutional flows (A/D Line, OBV), market breadth (advance-decline, % above 50MA), sector leadership (XLF, XLI vs S&P 500), and panic indicators (VIX, put/call, volume) for comprehensive market status assessment"
                    placement="top"
                    size="small"
                  />
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vectors.map((vector) => (
              <TableRow 
                key={vector[0]} 
                sx={{ 
                  backgroundColor: getRowColor(vector[2]), 
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#f5f5f5', // Change background color on hover
                  }
                }}
                onClick={() => handleRowClick(vector[1])}
              >
                <TableCell 
  sx={{ 
    fontSize: '8px', 
    padding: '0.2px', 
    whiteSpace: 'normal', 
    width: '100px', // Adjust width as needed
    minWidth: '100px', // Ensures a minimum width
    maxWidth: '200px' // Prevents excessive expansion
  }} 
  component="th" 
  scope="row"
>
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    {vector[1]}
    {(() => {
      const changeIndicator = getVectorChangeIndicator(vector[1], vector[2]);
      return changeIndicator && (
        <Typography
          title={changeIndicator.tooltip}
          sx={{
            fontSize: '12px',
            color: changeIndicator.color,
            fontWeight: 'bold',
            ml: 0.5,
            cursor: 'help',
            textShadow: '0 0 2px rgba(0,0,0,0.3)'
          }}
        >
          {changeIndicator.symbol}
        </Typography>
      );
    })()}
  </Box>
</TableCell>

                <TableCell
                  sx={{ fontSize: '8px', padding: '0.2px', whiteSpace: 'normal', cursor: fundamentalContrarianTooltip(vector[2]) ? 'help' : 'inherit' }}
                  align="left"
                  title={fundamentalContrarianTooltip(vector[2]) || undefined}
                >
                  {vector[2]}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

<MarketAnalysisModal
  open={open}
  onClose={() => setOpen(false)}
  selectedVector={selectedVector}
  analysisData={analysisData}
/>

    </>
  );
};

export default VectorsTable;
